import { useRef, useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, CheckCircle2, Download, FileSpreadsheet, Link2, Loader2, MinusCircle, RefreshCw, Upload, UserPlus, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import api, { apiError } from "@/lib/api";
import { cn } from "@/lib/utils";

const ST = {
  new: ["New", "bg-emerald-50 text-emerald-800 ring-emerald-200", UserPlus],
  update: ["Update", "bg-sky-50 text-sky-800 ring-sky-200", RefreshCw],
  link: ["Link", "bg-indigo-50 text-indigo-800 ring-indigo-200", Link2],
  unchanged: ["No change", "bg-slate-50 text-slate-600 ring-slate-200", MinusCircle],
  error: ["Error", "bg-rose-50 text-rose-800 ring-rose-200", AlertTriangle],
};
const MAX = 5 * 1024 * 1024;

const save = (blob, name) => { const u = URL.createObjectURL(blob); const a = document.createElement("a"); a.href = u; a.download = name; a.click(); setTimeout(() => URL.revokeObjectURL(u), 1000); };
const form = (file, extra = {}) => { const f = new FormData(); f.append("file", file); Object.entries(extra).forEach(([k, v]) => f.append(k, v)); return f; };

const Badge = ({ s }) => { const [l, c, Icon] = ST[s]; return <span className={cn("inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-bold ring-1", c)}><Icon className="h-3 w-3" />{l}</span>; };

const PreviewRow = ({ r }) => (
  <li className="flex flex-col gap-1 px-3 py-2.5 sm:flex-row sm:items-start sm:gap-3" data-testid={`import-row-${r.row}`}>
    <div className="flex shrink-0 items-center gap-2 sm:w-32"><span className="w-10 text-[11px] tabular-nums text-slate-400">Row {r.row}</span><Badge s={r.status} /></div>
    <div className="min-w-0 flex-1">
      <div className="break-words text-sm font-medium text-slate-900">{r.name || "—"} <span className="font-normal text-slate-500">· {r.email || "no email"}</span></div>
      <div className="text-xs text-slate-500">{r.role_label}</div>
      {r.errors.map((e) => <div key={e} className="text-xs font-medium text-rose-700">{e}</div>)}
      {r.changes.map((c) => <div key={c} className="text-xs text-sky-800">{c}</div>)}
    </div>
  </li>
);

const Result = ({ res, onClose }) => (
  <div className="space-y-3 rounded-xl bg-emerald-50/60 p-4 ring-1 ring-emerald-200" data-testid="import-result">
    <div className="flex items-start justify-between gap-2">
      <div className="flex items-center gap-2 text-sm font-semibold text-emerald-900"><CheckCircle2 className="h-5 w-5" /> Import finished</div>
      <button type="button" onClick={onClose} aria-label="Close" className="rounded-full p-1 text-slate-500 hover:bg-white" data-testid="import-result-close"><X className="h-4 w-4" /></button>
    </div>
    <div className="flex flex-wrap gap-2 text-xs font-semibold">
      {[["created", "Created"], ["updated", "Updated"], ["linked", "Linked"], ["skipped", "Skipped"]].map(([k, l]) => <span key={k} className="rounded-full bg-white px-2.5 py-1 ring-1 ring-slate-200" data-testid={`import-result-${k}`}>{l} {res.counts[k]}</span>)}
      {res.invites > 0 && <span className="rounded-full bg-white px-2.5 py-1 text-teal-800 ring-1 ring-teal-200" data-testid="import-result-invites">{res.invites} invite emails sent</span>}
    </div>
    {["created", "updated", "linked", "skipped"].filter((k) => res[k].length).map((k) => (
      <details key={k} className="rounded-lg bg-white px-3 py-2 ring-1 ring-slate-200" open={k !== "skipped"}>
        <summary className="cursor-pointer text-xs font-semibold capitalize text-slate-700">{k} ({res[k].length})</summary>
        <ul className="mt-1 max-h-48 space-y-0.5 overflow-y-auto text-xs text-slate-600">
          {res[k].map((x) => <li key={`${x.row}-${x.email}`} className="break-words">Row {x.row} · <span className="font-medium text-slate-800">{x.name}</span> · {x.email}{x.reason && <span className="text-rose-700"> — {x.reason}</span>}{x.changes?.length > 0 && <span className="text-sky-800"> — {x.changes.join("; ")}</span>}</li>)}
        </ul>
      </details>
    ))}
  </div>
);

/** Bulk import of task users from the Excel template: preview first, then import the valid rows. */
export const UserImport = ({ cid, slug, onImported }) => {
  const input = useRef(null);
  const [file, setFile] = useState(null);
  const [pre, setPre] = useState(null);
  const [invites, setInvites] = useState(true);
  const [busy, setBusy] = useState("");
  const [res, setRes] = useState(null);
  const base = `/admin/companies/${cid}/users/import`;
  const run = async (k, fn) => { setBusy(k); try { await fn(); } catch (e) { toast.error(apiError(e)); } finally { setBusy(""); } };

  const template = () => run("tpl", async () => save((await api.get(`${base}/template`, { responseType: "blob" })).data, `${slug || "company"}-users-template.xlsx`));
  const pick = (f) => {
    if (input.current) input.current.value = "";
    if (!f) return;
    if (!/\.xlsx$/i.test(f.name)) { toast.error("Choose an .xlsx file (the template)"); return; }
    if (f.size > MAX) { toast.error("The file can be up to 5 MB"); return; }
    setRes(null);
    run("pre", async () => { setPre((await api.post(`${base}/preview`, form(f))).data); setFile(f); });
  };
  const report = () => run("err", async () => save((await api.post(`${base}/errors`, form(file), { responseType: "blob" })).data, `${slug || "company"}-users-import-errors.xlsx`));
  const commit = () => run("go", async () => {
    const r = (await api.post(`${base}/commit`, form(file, { send_invites: invites }))).data;
    setRes(r); setPre(null); setFile(null); toast.success(`${r.counts.created} created · ${r.counts.updated} updated`); onImported?.();
  });
  const s = pre?.summary;
  const valid = s ? s.new + s.update + s.link : 0;

  return (
    <section className="space-y-4 rounded-xl border border-slate-200 bg-white p-4 sm:p-5" data-testid="user-import">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex gap-3">
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-emerald-100 text-emerald-700"><FileSpreadsheet className="h-5 w-5" /></span>
          <div><h2 className="text-sm font-semibold text-slate-900">Bulk import from Excel</h2>
            <p className="text-xs text-slate-500">Download the template, fill one user per row, then upload. You'll see a preview before anything is saved. Up to 1,000 users · 5 MB.</p></div>
        </div>
        <div className="grid grid-cols-2 gap-2 sm:flex">
          <Button variant="outline" className="h-10 gap-1.5" onClick={template} disabled={!!busy} data-testid="import-template-button">{busy === "tpl" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />} Template</Button>
          <Button className="h-10 gap-1.5 bg-teal-600 hover:bg-teal-700" onClick={() => input.current?.click()} disabled={!!busy} data-testid="import-upload-button">{busy === "pre" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} Upload .xlsx</Button>
          <input ref={input} type="file" accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" className="hidden" onChange={(e) => pick(e.target.files?.[0])} data-testid="import-file-input" />
        </div>
      </div>

      {pre && (
        <div className="space-y-3" data-testid="import-preview">
          <div className="flex flex-wrap items-center gap-2 text-xs font-semibold">
            <span className="text-slate-500" data-testid="import-file-name">{file?.name} · {s.total} rows</span>
            {Object.keys(ST).map((k) => <span key={k} className={cn("rounded-full px-2.5 py-1 ring-1", ST[k][1])} data-testid={`import-count-${k}`}>{ST[k][0]} {s[k]}</span>)}
          </div>
          <ul className="max-h-[55vh] divide-y divide-slate-100 overflow-y-auto rounded-lg ring-1 ring-slate-200">
            {pre.rows.length === 0 && <li className="px-3 py-6 text-center text-sm text-slate-500">No user rows found in this file</li>}
            {pre.rows.map((r) => <PreviewRow key={r.row} r={r} />)}
          </ul>
          <label className="flex items-center gap-2 text-sm text-slate-700"><Checkbox checked={invites} onCheckedChange={(v) => setInvites(!!v)} data-testid="import-send-invites" /> Send invite emails to new users (link to set their own password)</label>
          <div className="grid gap-2 sm:flex sm:flex-wrap">
            <Button className="h-11 gap-1.5 bg-teal-600 hover:bg-teal-700" disabled={!valid || !!busy} onClick={commit} data-testid="import-commit-button">{busy === "go" ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />} Import valid rows only ({valid})</Button>
            {s.error > 0 && <Button variant="outline" className="h-11 gap-1.5 border-rose-200 text-rose-700 hover:bg-rose-50" disabled={!!busy} onClick={report} data-testid="import-error-report-button">{busy === "err" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />} Error report</Button>}
            <Button variant="ghost" className="h-11" disabled={!!busy} onClick={() => { setPre(null); setFile(null); }} data-testid="import-cancel-button">Cancel</Button>
          </div>
        </div>
      )}
      {res && <Result res={res} onClose={() => setRes(null)} />}
    </section>
  );
};
